
import { useState, useEffect } from "react";
import { getCalendarConnection } from "@/lib/calendar";
import { debugLog, debugError } from "@/lib/debug-utils";
import { CalendarNotificationService } from "@/lib/calendar/notification-integration";
import { connectionHealthMonitor } from "@/lib/calendar/connection-health-monitor";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook for checking calendar connection status with enhanced validation and health monitoring
 */
export function useCalendarConnection(hostUserId?: string) {
  const [hasConnection, setHasConnection] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionCheckAttempts, setConnectionCheckAttempts] = useState(0);
  const [lastValidationTime, setLastValidationTime] = useState<number | null>(null);
  const [lastKnownConnectionState, setLastKnownConnectionState] = useState<boolean | null>(null);

  const MAX_ATTEMPTS = 3;
  const VALIDATION_CACHE_TIME = 60000; // 1 minute cache for connection validation

  useEffect(() => {
    async function checkConnection() {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`🔍 [useCalendarConnection] Starting connection check for ${hostUserId ? `host ${hostUserId}` : 'current user'}`);
        
        // Check if we have a recent validation result
        const now = Date.now();
        if (lastValidationTime && (now - lastValidationTime) < VALIDATION_CACHE_TIME) {
          debugLog("[useCalendarConnection] Using cached validation result");
          setLoading(false);
          return;
        }

        debugLog(`[useCalendarConnection] Checking calendar connection (attempt ${connectionCheckAttempts + 1})${hostUserId ? ` for host ${hostUserId}` : ''}`);
        
        if (hostUserId === 'debug') {
          debugLog("[useCalendarConnection] Debug mode detected, setting connection to true");
          setHasConnection(true);
          setLoading(false);
          setLastValidationTime(now);
          return;
        }
        
        let connectionResult = false;
        let connectionError: string | null = null;

        try {
          // Check calendar connection for specific host or current user
          if (hostUserId && hostUserId !== 'debug') {
            console.log(`📊 [useCalendarConnection] Checking host ${hostUserId} calendar connections`);
            
            // Check host's calendar connection directly from database
            // Don't filter by token_health_status to avoid race conditions with background token validation
            // Instead, check if ANY connection exists for the host
            const { data: connections, error } = await supabase
              .from('calendar_connections')
              .select('id, user_id, provider, token_health_status, created_at')
              .eq('user_id', hostUserId);
            
            if (error) {
              console.error(`❌ [useCalendarConnection] Database error for host ${hostUserId}:`, error);
              throw new Error(`Failed to check host calendar connection: ${error.message}`);
            }
            
            // Consider connection exists if there are any connections
            // Background services will handle token validation and healing
            // This prevents false negatives during initial page loads
            connectionResult = connections && connections.length > 0;
            
            // Log a warning if connections exist but none are healthy
            const healthyConnections = connections?.filter(c => c.token_health_status === 'healthy') || [];
            if (connections && connections.length > 0 && healthyConnections.length === 0) {
              console.warn(`⚠️ [useCalendarConnection] Host ${hostUserId} has ${connections.length} connection(s) but none are marked as healthy. Background services will validate.`);
            }
            
            console.log(`📊 [useCalendarConnection] Host ${hostUserId} connection check result:`, {
              connectionResult,
              totalConnections: connections?.length || 0,
              healthyConnections: healthyConnections.length,
              connections: connections?.map(c => ({
                id: c.id,
                provider: c.provider,
                status: c.token_health_status,
                created: c.created_at
              })) || []
            });
          } else {
            console.log(`📊 [useCalendarConnection] Checking current user's calendar connection`);
            
            // Check current user's connection
            const connection = await getCalendarConnection();
            connectionResult = !!connection;
            console.log(`📊 [useCalendarConnection] Current user connection check result:`, {
              connectionResult,
              hasConnection: !!connection
            });
          }
          
          // Start health monitoring if connection exists
          if (connectionResult && hostUserId && hostUserId !== 'debug') {
            connectionHealthMonitor.startMonitoring(hostUserId);
          }
        } catch (e: any) {
          connectionError = e.message || "Failed to check calendar connection";
          console.error(`❌ [useCalendarConnection] Connection check error:`, e);
          debugError("[useCalendarConnection] Connection check error:", e);
        }

        if (connectionError) {
          throw new Error(connectionError);
        }

        console.log(`✅ [useCalendarConnection] Final connection result: ${connectionResult}`);
        
        // Update the last known connection state when we get a successful result
        if (connectionResult !== null) {
          setLastKnownConnectionState(connectionResult);
        }
        
        setHasConnection(connectionResult);
        setLastValidationTime(now);

        // If connection is invalid and we haven't reached max attempts, retry
        if (!connectionResult && connectionCheckAttempts < MAX_ATTEMPTS) {
          debugLog(`[useCalendarConnection] Connection invalid, scheduling retry (attempt ${connectionCheckAttempts + 1}/${MAX_ATTEMPTS})`);
          setTimeout(() => {
            setConnectionCheckAttempts(prev => prev + 1);
          }, 2000 * (connectionCheckAttempts + 1)); // Exponential backoff
          return;
        }

        // Log connection failure but don't send alerts for temporary issues
        if (!connectionResult && hostUserId && hostUserId !== 'debug') {
          console.warn(`⚠️ [useCalendarConnection] No healthy calendar connection found for host ${hostUserId}`);
          debugLog("[useCalendarConnection] Connection validation failed - automatic retry will handle this");
        }
        
      } catch (e: any) {
        console.error(`💥 [useCalendarConnection] Error checking calendar connection:`, e);
        debugError("[useCalendarConnection] Error checking calendar connection:", e);
        const errorMessage = e.message || "Failed to check calendar connection";
        setError(errorMessage);
        
        // Check if this is a network error (like ERR_INTERNET_DISCONNECTED)
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                              errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
                              errorMessage.includes('NetworkError') ||
                              errorMessage.includes('fetch');
        
        if (isNetworkError) {
          console.log(`🌐 [useCalendarConnection] Network error detected, preserving last known connection state`);
          
          // If we have a last known connection state, use it instead of assuming disconnected
          if (lastKnownConnectionState !== null) {
            console.log(`🔄 [useCalendarConnection] Using last known connection state: ${lastKnownConnectionState}`);
            setHasConnection(lastKnownConnectionState);
            setError(null); // Clear the error since it's just a network issue
          } else {
            // If we don't have a previous state, don't assume disconnected
            console.log(`❓ [useCalendarConnection] No previous connection state known, keeping current state`);
            // Don't change hasConnection - keep whatever it was before
          }
        } else if (connectionCheckAttempts < MAX_ATTEMPTS) {
          debugLog(`[useCalendarConnection] Retrying connection check (attempt ${connectionCheckAttempts + 1}/${MAX_ATTEMPTS})`);
          setTimeout(() => {
            setConnectionCheckAttempts(prev => prev + 1);
          }, 2000 * (connectionCheckAttempts + 1));
          return;
        } else {
          // For development or demo purposes, proceed with caution
          if (process.env.NODE_ENV === 'development') {
            debugLog("[useCalendarConnection] Development mode - setting connection to false with error");
            setHasConnection(false);
          } else {
            setHasConnection(false);
          }
        }
      } finally {
        // Only update loading state if component is still mounted
        setLoading(false);
      }
    }
    
    checkConnection();
    
    // Cleanup health monitoring on unmount
    return () => {
      if (hostUserId && hostUserId !== 'debug') {
        connectionHealthMonitor.stopMonitoring();
      }
    };
  }, [hostUserId, connectionCheckAttempts, lastKnownConnectionState]);

  // Force a fresh connection check
  const refreshConnection = () => {
    console.log(`🔄 [useCalendarConnection] Forcing refresh for ${hostUserId ? `host ${hostUserId}` : 'current user'}`);
    setLastValidationTime(null);
    setConnectionCheckAttempts(0);
    setError(null);
    setHasConnection(null);
  };

  return {
    hasConnection,
    loading,
    error,
    checkAttempts: connectionCheckAttempts,
    refreshConnection,
    isHealthy: hasConnection === true && !error
  };
}
